import React, { useEffect, useState, useCallback } from 'react';
import { FiRefreshCw, FiMail, FiCheckCircle, FiXCircle, FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import './MailLogs.css';

const TRIGGER_META = {
  'quotation':          { label: 'Quotation',         color: '#6366f1', bg: '#eef2ff' },
  'auto-absent':        { label: 'Auto Absent',        color: '#f59e0b', bg: '#fffbeb' },
  'holiday':            { label: 'Holiday',             color: '#10b981', bg: '#ecfdf5' },
  'manual-attendance':  { label: 'Manual Attendance',  color: '#3b82f6', bg: '#eff6ff' },
  'system':             { label: 'System',              color: '#64748b', bg: '#f1f5f9' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function TriggerBadge({ trigger }) {
  const m = TRIGGER_META[trigger] || { label: trigger, color: '#64748b', bg: '#f1f5f9' };
  return (
    <span className="ml-trigger-badge" style={{ color: m.color, background: m.bg, border: `1px solid ${m.color}30` }}>
      {m.label}
    </span>
  );
}

export default function MailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await API.get('/mail-logs/stats');
      setStats(data);
    } catch {}
  }, []);

  const fetchLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (triggerFilter) params.trigger = triggerFilter;
      const { data } = await API.get('/mail-logs', { params });
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(data.pages);
      setPage(pg);
    } catch {
      toast.error('Failed to load mail logs');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, triggerFilter]);

  useEffect(() => { fetchStats(); fetchLogs(1); }, [fetchStats, fetchLogs]);

  const deliveryRate = stats.total ? Math.round((stats.sent / stats.total) * 100) : 0;

  return (
    <Layout>
      {loading && <PageLoader text="Loading mail logs..." />}
      <div className="page-actions">
        <button className="btn btn-outline" onClick={() => { fetchStats(); fetchLogs(page); }}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="ml-stats">
        <div className="ml-stat-card">
          <div className="ml-stat-left ml-stat-left--blue">
            <FiMail size={22} />
          </div>
          <div className="ml-stat-body">
            <div className="ml-stat-num">{stats.total}</div>
            <div className="ml-stat-lbl">Total Emails</div>
          </div>
          <div className="ml-stat-sparkline ml-stat-sparkline--blue" />
        </div>

        <div className="ml-stat-card">
          <div className="ml-stat-left ml-stat-left--green">
            <FiCheckCircle size={22} />
          </div>
          <div className="ml-stat-body">
            <div className="ml-stat-num">{stats.sent}</div>
            <div className="ml-stat-lbl">Successfully Sent</div>
          </div>
          <div className="ml-stat-sparkline ml-stat-sparkline--green" />
        </div>

        <div className="ml-stat-card">
          <div className="ml-stat-left ml-stat-left--red">
            <FiXCircle size={22} />
          </div>
          <div className="ml-stat-body">
            <div className="ml-stat-num">{stats.failed}</div>
            <div className="ml-stat-lbl">Failed</div>
          </div>
          <div className="ml-stat-sparkline ml-stat-sparkline--red" />
        </div>

        <div className="ml-stat-card ml-stat-card--rate">
          <div className="ml-stat-left ml-stat-left--purple">
            <FiCheckCircle size={22} />
          </div>
          <div className="ml-stat-body">
            <div className="ml-stat-num">{deliveryRate}%</div>
            <div className="ml-stat-lbl">Delivery Rate</div>
            <div className="ml-rate-bar">
              <div className="ml-rate-fill" style={{ width: `${deliveryRate}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ml-toolbar">
        <form className="ml-search-box" onSubmit={e => { e.preventDefault(); fetchLogs(1); }}>
          <FiSearch className="ml-search-ico" />
          <input
            className="ml-search-inp"
            placeholder="Search recipient or subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>
        <div className="ml-toolbar-right">
          <select className="ml-sel" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <select className="ml-sel" value={triggerFilter} onChange={e => setTriggerFilter(e.target.value)}>
            <option value="">All Triggers</option>
            <option value="quotation">Quotation</option>
            <option value="auto-absent">Auto Absent</option>
            <option value="holiday">Holiday</option>
            <option value="manual-attendance">Manual Attendance</option>
            <option value="system">System</option>
          </select>
          <span className="ml-count">{total} record{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <div className="ml-card">
        {loading ? (
          <div className="ml-empty-state"><div className="ml-spinner" /><p>Loading…</p></div>
        ) : logs.length === 0 ? (
          <div className="ml-empty-state">
            <div className="ml-empty-icon"><FiMail size={32} /></div>
            <p className="ml-empty-title">No mail logs found</p>
            <p className="ml-empty-sub">Emails sent by the system will appear here</p>
          </div>
        ) : (
          <table className="ml-table">
            <thead>
              <tr>
                <th className="ml-th-num">#</th>
                <th>Recipient</th>
                <th>Customer</th>
                <th>Subject</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Sent At</th>
                <th className="ml-th-act"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <React.Fragment key={log._id}>
                  <tr
                    className={`ml-tr ${log.status === 'failed' ? 'ml-tr--failed' : ''} ${expanded === log._id ? 'ml-tr--expanded' : ''}`}
                    onClick={() => setExpanded(expanded === log._id ? null : log._id)}
                  >
                    <td className="ml-td-num">{(page - 1) * 50 + i + 1}</td>
                    <td className="ml-td-email">
                      <div className="ml-email-pill">
                        <div className="ml-avatar">{(log.to?.[0] || '?').toUpperCase()}</div>
                        <span>{log.to}</span>
                      </div>
                    </td>
                    <td className="ml-td-customer">
                      {log.customer ? (
                        <span className="ml-customer-name">{log.customer}</span>
                      ) : (
                        <span className="ml-dash">—</span>
                      )}
                    </td>
                    <td className="ml-td-subject">{log.subject}</td>
                    <td><TriggerBadge trigger={log.trigger} /></td>
                    <td>
                      {log.status === 'sent' ? (
                        <span className="ml-status ml-status--sent"><FiCheckCircle size={12} /> Sent</span>
                      ) : (
                        <span className="ml-status ml-status--failed"><FiXCircle size={12} /> Failed</span>
                      )}
                    </td>
                    <td className="ml-td-date">{fmtDate(log.sentAt)}</td>
                    <td className="ml-td-act">
                      {expanded === log._id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </td>
                  </tr>
                  {expanded === log._id && (
                    <tr className="ml-detail-tr">
                      <td colSpan={8}>
                        <div className="ml-detail-panel">
                          {log.messageId && (
                            <div className="ml-detail-item">
                              <span className="ml-detail-key">Message ID</span>
                              <span className="ml-detail-val ml-detail-mono">{log.messageId}</span>
                            </div>
                          )}
                          {log.response && (
                            <div className="ml-detail-item">
                              <span className="ml-detail-key">SMTP Response</span>
                              <span className="ml-detail-val ml-detail-mono">{log.response}</span>
                            </div>
                          )}
                          {log.error && (
                            <div className="ml-detail-item">
                              <span className="ml-detail-key">Error</span>
                              <span className="ml-detail-val ml-detail-error">{log.error}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ml-pagination">
          <span className="ml-page-info">Showing {logs.length} of {total} records</span>
          <div className="ml-page-btns">
            <button disabled={page <= 1} onClick={() => fetchLogs(page - 1)} className="ml-page-btn">← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, k) => {
              const p = page <= 3 ? k + 1 : page - 2 + k;
              if (p < 1 || p > totalPages) return null;
              return (
                <button key={p} onClick={() => fetchLogs(p)} className={`ml-page-btn ${p === page ? 'ml-page-btn--active' : ''}`}>{p}</button>
              );
            })}
            <button disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)} className="ml-page-btn">Next →</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
